import { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Plus, Search, Edit3, Trash2, Copy, X, Grid3X3, List,
  AlertTriangle, Image as ImageIcon, ChevronDown, Star, Tag, Zap, ToggleRight, ToggleLeft, Eye, Upload
} from 'lucide-react';
import { useCatalogStore, genId } from '../store/catalogStore';
import { toast } from '../components/Toast';
import { Pagination } from '../components/UIComponents';
import { paginate } from '../utils/helpers';
import type { CatalogProduct, ProductImage } from '../types/catalog';

const PER_PAGE = 12;

const PRODUCT_TYPES = [
  'Bullet Camera', 'Dome Camera', 'Turret Camera', 'PTZ Camera', 'Box Camera',
  'NVR', 'DVR', 'XVR', 'Hard Drive', 'Video Doorbell', 'Smart Door Lock',
  'Alarm Panel', 'PIR Sensor', 'Door Sensor', 'Window Sensor', 'Siren',
  'PoE Switch', 'Network Switch', 'Wi-Fi Router', 'Access Point',
  'Outdoor CPE', 'UPS', 'Power Supply', 'Cable', 'Connector', 'Bracket', 'Other',
];

const emptyProduct = (): Omit<CatalogProduct, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: '', brandId: '', modelNumber: '', sku: '', categoryId: '', subcategoryId: '',
  productType: '', shortDescription: '', fullDescription: '',
  mainImage: '', galleryImages: [],
  regularPrice: 0, salePrice: undefined, costPrice: undefined, installationPrice: undefined,
  stock: 0, reservedStock: 0, minStockAlert: 5, inStock: true,
  isFeatured: false, isNewProduct: false, isBestSeller: false,
  status: 'active', warrantyPeriod: '1 Year', specs: [],
  resolution: '', megapixels: undefined, lensSize: '', nightVisionDistance: '',
  lightingType: '', usage: 'Both', ipRating: '',
  hasAudio: false, hasTwoWayAudio: false, hasAIHumanDetection: false,
  hasVehicleDetection: false, hasMotionDetection: true, hasSmartTracking: false,
  hasWiFi: false, hasPOE: false, storageSupport: '', mobileAppSupport: '',
  powerRequirements: '', compatibleRecorder: '', installationNotes: '', datasheetUrl: '',
});

const isCameraType = (t?: string) =>
  ['Bullet Camera', 'Dome Camera', 'Turret Camera', 'PTZ Camera', 'Box Camera'].includes(t ?? '');

function formatLKR(v: number) {
  return `Rs. ${v.toLocaleString('en-LK')}`;
}

export default function CatalogProductsPage() {
  const { brands, categories, catalogProducts, addCatalogProduct, updateCatalogProduct, deleteCatalogProduct, duplicateCatalogProduct, updateProductImages } = useCatalogStore();

  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<CatalogProduct, 'id' | 'createdAt' | 'updatedAt'>>(emptyProduct());
  const [imageTab, setImageTab] = useState<'main' | 'gallery'>('main');
  const [galleryInput, setGalleryInput] = useState('');
  const mainImageRef = useRef<HTMLInputElement>(null);

  // ── Filtering ──────────────────────────────────────────────────────────────
  const topCategories = categories.filter((c) => !c.parentId);
  const subCategories = categories.filter((c) => !!c.parentId);

  const filtered = useMemo(() => {
    return catalogProducts.filter((p) => {
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.sku.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterBrand && p.brandId !== filterBrand) return false;
      if (filterCategory && p.categoryId !== filterCategory && p.subcategoryId !== filterCategory) return false;
      if (filterStatus === 'active' && p.status !== 'active') return false;
      if (filterStatus === 'inactive' && p.status !== 'inactive') return false;
      if (filterStatus === 'instock' && !p.inStock) return false;
      if (filterStatus === 'outofstock' && p.inStock) return false;
      return true;
    });
  }, [catalogProducts, search, filterBrand, filterCategory, filterStatus]);

  const { items: paged, pagination } = useMemo(() => paginate(filtered, page, PER_PAGE), [filtered, page]);

  // ── Form Helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(emptyProduct());
    setImageTab('main');
    setShowForm(true);
  };

  const openEdit = (p: CatalogProduct) => {
    setEditingId(p.id);
    setForm({
      name: p.name, brandId: p.brandId, modelNumber: p.modelNumber ?? '', sku: p.sku,
      categoryId: p.categoryId, subcategoryId: p.subcategoryId ?? '', productType: p.productType ?? '',
      shortDescription: p.shortDescription ?? '', fullDescription: p.fullDescription ?? '',
      mainImage: p.mainImage ?? '', galleryImages: p.galleryImages ?? [],
      regularPrice: p.regularPrice, salePrice: p.salePrice, costPrice: p.costPrice,
      installationPrice: p.installationPrice,
      stock: p.stock, reservedStock: p.reservedStock, minStockAlert: p.minStockAlert, inStock: p.inStock,
      isFeatured: p.isFeatured, isNewProduct: p.isNewProduct, isBestSeller: p.isBestSeller,
      status: p.status, warrantyPeriod: p.warrantyPeriod ?? '1 Year', specs: p.specs ?? [],
      resolution: p.resolution ?? '', megapixels: p.megapixels, lensSize: p.lensSize ?? '',
      nightVisionDistance: p.nightVisionDistance ?? '', lightingType: p.lightingType ?? '',
      usage: p.usage ?? 'Both', ipRating: p.ipRating ?? '',
      hasAudio: p.hasAudio ?? false, hasTwoWayAudio: p.hasTwoWayAudio ?? false,
      hasAIHumanDetection: p.hasAIHumanDetection ?? false, hasVehicleDetection: p.hasVehicleDetection ?? false,
      hasMotionDetection: p.hasMotionDetection ?? true, hasSmartTracking: p.hasSmartTracking ?? false,
      hasWiFi: p.hasWiFi ?? false, hasPOE: p.hasPOE ?? false,
      storageSupport: p.storageSupport ?? '', mobileAppSupport: p.mobileAppSupport ?? '',
      powerRequirements: p.powerRequirements ?? '', compatibleRecorder: p.compatibleRecorder ?? '',
      installationNotes: p.installationNotes ?? '', datasheetUrl: p.datasheetUrl ?? '',
    });
    setImageTab('main');
    setShowForm(true);
  };

  const handleMainImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, mainImage: ev.target?.result as string }));
    reader.readAsDataURL(file);
  };

  const addGalleryUrl = () => {
    if (!galleryInput.trim()) return;
    const img: ProductImage = {
      id: genId(), productId: editingId ?? 'new',
      url: galleryInput.trim(), isPrimary: false,
      displayOrder: form.galleryImages.length + 1,
    };
    setForm((f) => ({ ...f, galleryImages: [...f.galleryImages, img] }));
    setGalleryInput('');
  };

  const uploadGalleryImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img: ProductImage = {
          id: genId(), productId: editingId ?? 'new',
          url: ev.target?.result as string, isPrimary: false,
          displayOrder: form.galleryImages.length + 1,
        };
        setForm((f) => ({ ...f, galleryImages: [...f.galleryImages, img] }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (id: string) =>
    setForm((f) => ({ ...f, galleryImages: f.galleryImages.filter((i) => i.id !== id) }));

  const handleSave = () => {
    if (!form.name.trim()) { toast.error('Validation', 'Product name is required.'); return; }
    if (!form.sku.trim()) { toast.error('Validation', 'SKU is required.'); return; }
    if (form.regularPrice <= 0) { toast.error('Validation', 'Price must be greater than 0.'); return; }
    const now = new Date().toISOString();
    if (editingId) {
      updateCatalogProduct(editingId, { ...form, updatedAt: now });
      toast.success('Product Updated', `"${form.name}" has been updated.`);
    } else {
      addCatalogProduct({ id: genId(), ...form, createdAt: now, updatedAt: now });
      toast.success('Product Added', `"${form.name}" added to the catalog.`);
    }
    setShowForm(false);
  };

  const handleDelete = (p: CatalogProduct) => {
    if (!confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    deleteCatalogProduct(p.id);
    toast.success('Product Deleted', `"${p.name}" removed.`);
  };

  const brandName = (id: string) => brands.find((b) => b.id === id)?.name ?? '—';
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? '—';

  const lowStock = catalogProducts.filter((p) => p.stock - p.reservedStock <= p.minStockAlert).length;

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Product Catalog</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {catalogProducts.length} products · {lowStock > 0 && <span className="text-amber-500">{lowStock} low stock</span>}
          </p>
        </div>
        <button
          onClick={openAdd}
          className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search products, SKU..."
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
          />
        </div>

        <select
          value={filterBrand}
          onChange={(e) => { setFilterBrand(e.target.value); setPage(1); }}
          className="h-10 px-3 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none"
        >
          <option value="">All Brands</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>

        <select
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
          className="h-10 px-3 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none"
        >
          <option value="">All Categories</option>
          <optgroup label="Main Categories">
            {topCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </optgroup>
          <optgroup label="Subcategories">
            {subCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </optgroup>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="h-10 px-3 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="instock">In Stock</option>
          <option value="outofstock">Out of Stock</option>
        </select>

        <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
          <button onClick={() => setView('grid')} className={`px-3 py-2 ${view === 'grid' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-600'}`} aria-label="Grid view">
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button onClick={() => setView('list')} className={`px-3 py-2 ${view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-600'}`} aria-label="List view">
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Product Grid ──────────────────────────────────────────────────── */}
      {catalogProducts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200/60">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-[14px]">No products yet. Click "Add Product" to get started.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200/60">
          <Search className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-[14px]">No products match your filters.</p>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {paged.map((product) => (
            <div key={product.id} className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden group hover:shadow-md transition-shadow">
              <div className="relative aspect-square bg-gray-50">
                {product.mainImage ? (
                  <img src={product.mainImage} alt={product.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-10 h-10 text-gray-200" />
                  </div>
                )}
                {product.isFeatured && <span className="absolute top-2 left-2 bg-[#7FA86A] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Featured</span>}
                {product.isBestSeller && <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Best Seller</span>}
                {!product.inStock && (
                  <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                    <span className="bg-gray-900 text-white text-[10px] px-3 py-1 rounded-full font-semibold">Out of Stock</span>
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-[10px] text-gray-400 mb-0.5">{brandName(product.brandId)}</p>
                <p className="text-[13px] font-medium text-gray-900 truncate">{product.name}</p>
                <p className="text-[11px] text-gray-400 mb-2">{product.sku}</p>
                <div className="flex items-center justify-between">
                  <p className="text-[13px] font-semibold text-[#7FA86A]">{formatLKR(product.regularPrice)}</p>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${product.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                    {product.status}
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Stock: {product.stock - product.reservedStock}</p>
              </div>
              <div className="px-3 pb-3 flex gap-1">
                <button onClick={() => openEdit(product)} className="flex-1 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 text-[12px] text-gray-600 font-medium flex items-center justify-center gap-1">
                  <Edit3 className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => duplicateCatalogProduct(product.id)} className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center" aria-label="Duplicate">
                  <Copy className="w-3.5 h-3.5 text-gray-500" />
                </button>
                <button onClick={() => handleDelete(product)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center" aria-label="Delete">
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 text-left font-medium text-gray-500">Product</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Brand</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Category</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Price</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Stock</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((product) => (
                <tr key={product.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {product.mainImage ? (
                        <img src={product.mainImage} alt={product.name} className="w-10 h-10 rounded-lg object-contain border border-gray-100 bg-gray-50" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center"><Package className="w-5 h-5 text-gray-200" /></div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-gray-400 text-[11px]">{product.sku}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-500">{brandName(product.brandId)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-500">{categoryName(product.categoryId)}</td>
                  <td className="px-4 py-3 font-semibold text-[#7FA86A]">{formatLKR(product.regularPrice)}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={product.stock - product.reservedStock <= product.minStockAlert ? 'text-amber-500 font-medium' : 'text-gray-600'}>
                      {product.stock - product.reservedStock}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${product.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                      {product.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(product)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center" aria-label="Edit"><Edit3 className="w-4 h-4 text-gray-500" /></button>
                      <button onClick={() => duplicateCatalogProduct(product.id)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center" aria-label="Duplicate"><Copy className="w-4 h-4 text-gray-500" /></button>
                      <button onClick={() => handleDelete(product)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center" aria-label="Delete"><Trash2 className="w-4 h-4 text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <Pagination pagination={pagination} onPageChange={setPage} />
      )}

      {/* ── Add/Edit Drawer ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white h-full w-full max-w-2xl overflow-y-auto shadow-2xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            >
              {/* Drawer Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-[17px] font-semibold text-gray-900">
                  {editingId ? 'Edit Product' : 'Add New Product'}
                </h2>
                <button onClick={() => setShowForm(false)} className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center" aria-label="Close">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="px-6 py-6 space-y-8">
                {/* ── Section: Basic Info ──────────────────────────────────── */}
                <section>
                  <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Product Name *</label>
                      <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="e.g. Dahua 5MP Dome Camera" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">Brand *</label>
                        <select value={form.brandId} onChange={(e) => setForm((f) => ({ ...f, brandId: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                          <option value="">Select Brand</option>
                          {brands.filter(b => b.isActive).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">Model Number</label>
                        <input value={form.modelNumber} onChange={(e) => setForm((f) => ({ ...f, modelNumber: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="e.g. HAC-HDW1500T" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">SKU *</label>
                        <input value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="e.g. DAH-5MP-DOME-001" />
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">Product Type</label>
                        <select value={form.productType} onChange={(e) => setForm((f) => ({ ...f, productType: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                          <option value="">Select Type</option>
                          {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">Category</label>
                        <select value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value, subcategoryId: '' }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                          <option value="">Select Category</option>
                          {topCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">Subcategory</label>
                        <select value={form.subcategoryId} onChange={(e) => setForm((f) => ({ ...f, subcategoryId: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                          <option value="">Select Subcategory</option>
                          {subCategories.filter((c) => c.parentId === form.categoryId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Short Description</label>
                      <input value={form.shortDescription} onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="One-line product summary" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Full Description</label>
                      <textarea value={form.fullDescription} onChange={(e) => setForm((f) => ({ ...f, fullDescription: e.target.value }))} rows={4} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="Detailed product description..." />
                    </div>
                  </div>
                </section>

                {/* ── Section: Images ──────────────────────────────────────── */}
                <section>
                  <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Product Images</h3>
                  <div className="flex gap-2 mb-4">
                    <button onClick={() => setImageTab('main')} className={`px-4 py-2 rounded-xl text-[13px] font-medium transition-colors ${imageTab === 'main' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>Main Image</button>
                    <button onClick={() => setImageTab('gallery')} className={`px-4 py-2 rounded-xl text-[13px] font-medium transition-colors ${imageTab === 'gallery' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>Gallery ({form.galleryImages.length})</button>
                  </div>
                  {imageTab === 'main' ? (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 flex flex-col items-center gap-3">
                        {form.mainImage ? (
                          <img src={form.mainImage} alt="Main product" className="w-32 h-32 object-contain rounded-xl border border-gray-100" />
                        ) : (
                          <div className="w-32 h-32 bg-gray-50 rounded-xl flex items-center justify-center"><ImageIcon className="w-10 h-10 text-gray-200" /></div>
                        )}
                        <div className="flex gap-2">
                          <label className="h-9 px-4 rounded-xl bg-[#7FA86A] text-white text-[12px] font-medium cursor-pointer hover:bg-[#658C53] flex items-center gap-1.5">
                            <Upload className="w-3.5 h-3.5" /> Upload Image
                            <input type="file" accept="image/*" className="hidden" ref={mainImageRef} onChange={handleMainImageUpload} />
                          </label>
                          {form.mainImage && (
                            <button onClick={() => setForm((f) => ({ ...f, mainImage: '' }))} className="h-9 px-4 rounded-xl bg-red-50 text-red-500 text-[12px] font-medium hover:bg-red-100">Remove</button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1">Or paste image URL</label>
                        <input
                          value={form.mainImage}
                          onChange={(e) => setForm((f) => ({ ...f, mainImage: e.target.value }))}
                          placeholder="https://..."
                          className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {form.galleryImages.map((img) => (
                          <div key={img.id} className="relative w-20 h-20 rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
                            <img src={img.url} alt="" className="w-full h-full object-contain" />
                            <button onClick={() => removeGalleryImage(img.id)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px]" aria-label="Remove">×</button>
                          </div>
                        ))}
                        <label className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-300 gap-1">
                          <Plus className="w-5 h-5" />
                          <span className="text-[10px]">Upload</span>
                          <input type="file" accept="image/*" multiple className="hidden" onChange={uploadGalleryImage} />
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <input value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} placeholder="Or paste image URL..." className="flex-1 h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                        <button onClick={addGalleryUrl} className="h-10 px-4 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800">Add URL</button>
                      </div>
                    </div>
                  )}
                </section>

                {/* ── Section: Pricing ─────────────────────────────────────── */}
                <section>
                  <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Pricing</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Regular Price (Rs.) *</label>
                      <input type="number" value={form.regularPrice} onChange={(e) => setForm((f) => ({ ...f, regularPrice: +e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Sale Price (Rs.)</label>
                      <input type="number" value={form.salePrice ?? ''} onChange={(e) => setForm((f) => ({ ...f, salePrice: e.target.value ? +e.target.value : undefined }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="Optional" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Cost Price (Rs.) — Admin Only</label>
                      <input type="number" value={form.costPrice ?? ''} onChange={(e) => setForm((f) => ({ ...f, costPrice: e.target.value ? +e.target.value : undefined }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="Optional" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Installation Price (Rs.)</label>
                      <input type="number" value={form.installationPrice ?? ''} onChange={(e) => setForm((f) => ({ ...f, installationPrice: e.target.value ? +e.target.value : undefined }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="Optional" />
                    </div>
                  </div>
                </section>

                {/* ── Section: Inventory ───────────────────────────────────── */}
                <section>
                  <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Inventory</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Stock Qty</label>
                      <input type="number" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: +e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Min Stock Alert</label>
                      <input type="number" value={form.minStockAlert} onChange={(e) => setForm((f) => ({ ...f, minStockAlert: +e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.inStock} onChange={(e) => setForm((f) => ({ ...f, inStock: e.target.checked }))} className="w-4 h-4 accent-[#7FA86A]" />
                        <span className="text-[13px] text-gray-600">In Stock</span>
                      </label>
                    </div>
                  </div>
                </section>

                {/* ── Section: Flags & Status ──────────────────────────────── */}
                <section>
                  <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Flags & Status</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Product Status</label>
                      <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as any }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Warranty Period</label>
                      <input value={form.warrantyPeriod} onChange={(e) => setForm((f) => ({ ...f, warrantyPeriod: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 2 Years" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3">
                    {[
                      { key: 'isFeatured', label: 'Featured Product' },
                      { key: 'isNewProduct', label: 'New Product' },
                      { key: 'isBestSeller', label: 'Best Seller' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={(form as any)[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))} className="w-4 h-4 accent-[#7FA86A]" />
                        <span className="text-[13px] text-gray-600">{label}</span>
                      </label>
                    ))}
                  </div>
                </section>

                {/* ── Section: Camera Specs (shown for camera types) ────────── */}
                {isCameraType(form.productType) && (
                  <section>
                    <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Camera Specifications</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Resolution</label>
                          <input value={form.resolution} onChange={(e) => setForm((f) => ({ ...f, resolution: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 4K 8MP" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Megapixels</label>
                          <input type="number" value={form.megapixels ?? ''} onChange={(e) => setForm((f) => ({ ...f, megapixels: e.target.value ? +e.target.value : undefined }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 8" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Lens Size</label>
                          <input value={form.lensSize} onChange={(e) => setForm((f) => ({ ...f, lensSize: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 2.8mm / 4mm" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Night Vision Distance</label>
                          <input value={form.nightVisionDistance} onChange={(e) => setForm((f) => ({ ...f, nightVisionDistance: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 30m" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Lighting Type</label>
                          <select value={form.lightingType} onChange={(e) => setForm((f) => ({ ...f, lightingType: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                            <option value="">Select</option>
                            <option>IR</option>
                            <option>Full Color</option>
                            <option>Smart Dual Light</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Usage</label>
                          <select value={form.usage} onChange={(e) => setForm((f) => ({ ...f, usage: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none bg-white">
                            <option>Both</option>
                            <option>Indoor</option>
                            <option>Outdoor</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">IP Rating</label>
                          <input value={form.ipRating} onChange={(e) => setForm((f) => ({ ...f, ipRating: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. IP67" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Storage Support</label>
                          <input value={form.storageSupport} onChange={(e) => setForm((f) => ({ ...f, storageSupport: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. NVR / SD Card" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Mobile App</label>
                          <input value={form.mobileAppSupport} onChange={(e) => setForm((f) => ({ ...f, mobileAppSupport: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. DMSS, IMOU Life" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Power Requirements</label>
                          <input value={form.powerRequirements} onChange={(e) => setForm((f) => ({ ...f, powerRequirements: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. 12VDC 1A / PoE" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1">Compatible Recorder</label>
                          <input value={form.compatibleRecorder} onChange={(e) => setForm((f) => ({ ...f, compatibleRecorder: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="e.g. Dahua NVR 4-ch" />
                        </div>
                      </div>
                      {/* Feature Checkboxes */}
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        {[
                          { key: 'hasAudio', label: 'Built-in Microphone' },
                          { key: 'hasTwoWayAudio', label: 'Two-Way Audio' },
                          { key: 'hasAIHumanDetection', label: 'AI Human Detection' },
                          { key: 'hasVehicleDetection', label: 'Vehicle Detection' },
                          { key: 'hasMotionDetection', label: 'Motion Detection' },
                          { key: 'hasSmartTracking', label: 'Smart Tracking' },
                          { key: 'hasWiFi', label: 'Wi-Fi Support' },
                          { key: 'hasPOE', label: 'PoE Support' },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={(form as any)[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))} className="w-4 h-4 accent-[#7FA86A]" />
                            <span className="text-[13px] text-gray-600">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </section>
                )}

                {/* ── Section: Additional Info ─────────────────────────────── */}
                <section>
                  <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Additional Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Installation Notes</label>
                      <textarea value={form.installationNotes} onChange={(e) => setForm((f) => ({ ...f, installationNotes: e.target.value }))} rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-gray-500 mb-1">Datasheet URL</label>
                      <input value={form.datasheetUrl} onChange={(e) => setForm((f) => ({ ...f, datasheetUrl: e.target.value }))} className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none" placeholder="https://..." />
                    </div>
                  </div>
                </section>
              </div>

              {/* Drawer Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
                <button onClick={() => setShowForm(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
                <button onClick={handleSave} className="h-10 px-6 rounded-xl bg-[#7FA86A] text-white text-[13px] font-semibold hover:bg-[#658C53]">
                  {editingId ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
